import { CircularProgress } from '@mui/material';
import React, { Fragment, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isUserAdmin, useAuth } from 'src/components/pages/Auth/AuthContext';
import styled from 'styled-components';
import { Icon } from '../Icon/Icon';

type ButtonType = 'submit' | 'reset' | 'button';

export type ButtonProps = {
  label?: string;
  type?: ButtonType;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  children?: ReactNode;
  disabled?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
  icon?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

export const PrimaryButton: React.FC<ButtonProps> = ({
  label,
  onClick,
  children,
  disabled,
  fullWidth,
  loading,
  className,
  type,
  icon,
  ...rest
}) => (
  <PrimaryButtonBase
    type={type}
    onClick={onClick}
    fullWidth={fullWidth}
    disabled={disabled}
    $loading={loading}
    className={className ? className : ''}
    {...rest}
  >
    <Fragment>
      {icon && <Icon type={icon} />}
      <span>{children ? children : label}</span>
    </Fragment>
    {loading && (
      <LoadingContainer>
        <CircularProgress color="inherit" size={24} />
      </LoadingContainer>
    )}
  </PrimaryButtonBase>
);

export const SecondaryButton: React.FC<ButtonProps> = ({
  label,
  onClick,
  children,
  disabled,
  loading,
  fullWidth,
  className,
  icon,
  ...rest
}) => (
  <SecondaryButtonBase
    type="button"
    onClick={onClick}
    fullWidth={fullWidth}
    disabled={disabled}
    $loading={loading}
    className={className ? className : ''}
    {...rest}
  >
    <Fragment>
      {icon && <Icon type={icon} />}
      <span>{children ? children : label}</span>
    </Fragment>
    {loading && (
      <LoadingContainer>
        <CircularProgress color="inherit" size={24} />
      </LoadingContainer>
    )}
  </SecondaryButtonBase>
);

export const TertiaryButton: React.FC<ButtonProps> = ({
  label,
  onClick,
  children,
  disabled,
  fullWidth,
  className,
  ...rest
}) => (
  <TertiaryButtonBase
    type="button"
    onClick={onClick}
    fullWidth={fullWidth}
    disabled={disabled}
    className={className ? className : ''}
    {...rest}
  >
    <span>{children ? children : label}</span>
  </TertiaryButtonBase>
);

export const ActionButton: React.FC<ButtonProps> = ({
  onClick,
  icon,
  disabled,
  className,
  ...rest
}) => (
  <ActionButtonBase
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={className ? className : ''}
    {...rest}
  >
    <Icon type={icon!} />
  </ActionButtonBase>
);

// The onClick logic is all that is mandatory for the button
export const DeleteButton: React.FC<ButtonProps> = ({
  onClick,
  className,
  title,
  ...rest
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  return (
    <ActionButtonBase
      type="button"
      onClick={onClick}
      className={className ? className : 'delete-button'}
      disabled={!isUserAdmin(user)}
      title={
        title
          ? title
          : isUserAdmin(user)
          ? t('delete.verb')
          : t('delete.notAdmin')
      }
      {...rest}
    >
      <Icon type="la la-trash" />
    </ActionButtonBase>
  );
};

export const BaseButton = styled.button<{
  fullWidth?: boolean;
  $loading?: boolean;
}>`
  font-family: Roboto;
  font-size: 1rem;
  line-height: 1.5rem;
  height: 2.5rem;
  max-height: 2.5rem;
  cursor: pointer;
  font-weight: 400;
  text-align: center;
  vertical-align: middle;
  border: 0;
  border-radius: 0.5rem;
  padding: 0.375rem 1rem;
  user-select: none;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  position: relative;

  span {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  width: ${props => (props.fullWidth ? '100%' : 'auto')};

  i {
    margin-right: 0.5rem;
  }

  &:focus {
    outline: 0;
    &:after {
      content: '';
      display: block;
      position: absolute;
      top: -4px;
      left: -4px;
      right: -4px;
      bottom: -4px;
      border-radius: 0.5rem;
      border: 2px solid var(--primary-color20);
    }
  }

  &:hover,
  &:active {
    outline: none;
  }

  &:disabled {
    opacity: 0.8;
    cursor: not-allowed;
  }
`;

const PrimaryButtonBase = styled(BaseButton)`
  background-color: var(--primary-color);
  color: #fff;

  &:hover:enabled,
  &:active:enabled {
    background-color: var(--primary-color-darkest);
  }

  ${props =>
    props.$loading &&
    `
    color: var(--primary-color);
  `}
`;

const SecondaryButtonBase = styled(BaseButton)`
  position: relative;
  z-index: 1;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  transition: color 0.15s;
  overflow: hidden;
  background: transparent;

  &:enabled:before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: -1;
    width: 100%;
    height: 0;
    background: var(--primary-color);
    transition: all 0.15s ease-out;
  }

  &:enabled:hover {
    color: #fff;

    &:before {
      height: 100%;
    }
  }

  &:disabled {
    border: 1px solid var(--primary-color60);
    color: var(--primary-color60);
  }

  ${props =>
    props.$loading &&
    `
    color: #fff;
  `}
`;

const TertiaryButtonBase = styled(BaseButton)`
  border: 1px solid transparent;
  color: var(--primary-color);

  span:after {
    content: '';
    display: block;
    position: relative;
    top: 4px;
    height: 1px;
    width: 0;
    left: 50%;
    background: var(--primary-color);
    transition: width 0.3s, left 0.3s;
  }

  &:hover:enabled,
  &:active:enabled {
    span:after {
      width: 100%;
      left: 0;
    }
  }
`;

const ActionButtonBase = styled(BaseButton)`
  position: relative;
  width: 40px;
  height: 40px;
  min-height: 40px;
  background-color: transparent;
  border-radius: 50%;
  color: var(--primary-color);
  padding: 0 !important;

  & i {
    margin-right: 1px;
    font-size: 1.5rem;
    line-height: 2rem;
  }

  &:active,
  &:hover {
    background-color: rgba(var(--primary-color-rgb), 0.2);
    outline: none;
    border: none;
  }

  &:disabled,
  &:disabled:hover {
    color: rgba(var(--primary-color-rgb), 0.4);
    background-color: transparent;
  }

  &:focus {
    &:after {
      border-radius: 50%;
    }
  }
`;

const LoadingContainer = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);
`;
